import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2, CheckCircle, XCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { transactionRedeem, dynamicAPI } from "@/services/customer";
import { useTranslation } from "react-i18next";


const UpiConfirmPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { upiId, upiName, defaultBank } = location.state || {};
  const { data: customer, loading } = useSelector((state) => state.customer);

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    dispatch(fetchCustomerData(customerCode));
  }, []);

  const available = customer?.walletsDetail?.TotalavailableValue || 0;

  const [loadings, setLoadings] = useState(false);
  const [popup, setPopup] = useState(null); // { type: "success" | "error" | "minBalance", amount, message }

  const handleFinalSubmit = async () => {

    if (available <= 0) {
      setPopup({ type: "minBalance" });
      return;
    }

    setLoadings(true);

    try {
      const token_gen = JSON.parse(localStorage.getItem("token_gen"));
      const customerCode = localStorage.getItem("customercode");
      const now = new Date().toISOString();

      const redeemBody = {
        customer: { customercode: customerCode },
        redeemDetail: {
          walletType: "point",
          values: available,
          transactionType: "redeem",
          transactionDate: new Date().toISOString().split("T")[0],
          bankdetails:upiId || defaultBank.bankName
        },
      };

      const redeemRes = await transactionRedeem(
        token_gen?.access_token,
        JSON.stringify(redeemBody)
      );

      if (redeemRes?.data?.result === 1) {
        try {
          const metaPayload = {
            customer: {
              customerCode,
              name: customer?.customer?.name,
              mobile: customer?.customer?.mobile,
            },
            redeem: {
              transactionId: redeemRes?.data?.currentTransactionId,
              redeemAmount: redeemRes?.data?.redeemAmount,
              walletType: redeemRes?.data?.walletType,
              responceMessage: redeemRes?.data?.responceMessage,
              wallets: redeemRes?.data?.Wallets?.map((w) => ({
                id: w.id,
                earnedValue: w.earnedValue,
                earnedTransactionId: w.earnedTransactionId,
                earnedDate: w.earnedDate,
                expiryDate: w.expiryDate,
              })),
            },
            payment: upiId
              ? {
                  type: "upi",
                  upiId,
                  upiName: upiName || "",
                  totalAmount: available,
                }
              : defaultBank
              ? {
                  type: "bank",
                  bankName: defaultBank.bankName,
                  accountNumber: defaultBank.accountNumber,
                  ifscCode: defaultBank.ifscCode,
                  beneficiaryName: defaultBank.nickname || defaultBank.bankName,
                  totalAmount: available,
                }
              : {},
            transactionDate: now,
            merchantId: customer?.customer?.merchantId?.[0],
          };

          await dynamicAPI(token_gen?.access_token, metaPayload);
        } catch (err) {
          console.error("Meta API failed", err);
        }

        await dispatch(fetchCustomerData(customerCode));

        setPopup({
          type: "success",
          amount: redeemRes?.data?.redeemAmount ?? available,
          message: redeemRes?.data?.responceMessage || t("payment.redeemedTitle"),
        });
      } else {
        setPopup({
          type: "error",
          message: t("voucher.redeemFailed"),
        });
      }
    } catch {
      setPopup({
        type: "error",
        message: t("common.somethingWrong"),
      });
    } finally {
      setLoadings(false);
    }
  };

  const handlePopupClose = () => {
    setPopup(null);
    navigate("/home", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex justify-center">
      {(loading || loadings) && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#1A1F36]" />
        </div>
      )}

      <div className="w-full max-w-md bg-white min-h-screen md:rounded-3xl md:shadow-lg">
        <div className="relative flex items-center justify-center border-b border-gray-100 px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full"
            style={{ backgroundColor: "#1A1F3610" }}
          >
            <ArrowLeft className="w-5 h-5 text-[#1A1F36]" />
          </button>
          <p className="text-base font-semibold text-[#1A1F36]">
            {t("payment.title")}
          </p>
        </div>

        <div className="px-4 pb-8 space-y-4 mt-4">
          {/* Amount Card */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1 text-center">
            <p className="text-[10px] font-semibold text-[rgb(120,130,160)] uppercase tracking-widest mb-1">
              {t("payment.transferAmount")}
            </p>
            <p className="text-[48px] font-bold text-[#1A1F36] leading-tight">₹{available}</p>
            {available === 0 && (
              <p className="text-[12px] text-red-400 mt-2">{t("payment.noBalance")}</p>
            )}
          </div>

          {/* UPI Detail */}
          {upiId && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
              <p className="text-[10px] font-semibold text-[rgb(120,130,160)] uppercase tracking-widest mb-3">
                {t("payment.paymentMethod")}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[rgb(226,230,240)] flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2d5a2d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5" />
                  </svg>
                </div>
                <div>
                  <p className="text-[13px] font-bold text-[#1a2e1a]">{t("payment.upi")}</p>
                  {upiName && <p className="text-[12px] text-gray-500 font-medium">{upiName}</p>}
                  <p className="text-[12px] text-gray-400">{upiId}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bank Detail */}
          {defaultBank && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1">
              <p className="text-[10px] font-semibold text-[rgb(120,130,160)] uppercase tracking-widest mb-3">
                {t("payment.paymentMethod")}
              </p>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[rgb(226,230,240)] flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2d5a2d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M16 12h.01" />
                  </svg>
                </div>
                <p className="text-[13px] font-bold text-[#1A1F36]">{t("payment.bankTransfer")}</p>
              </div>
              <div className="space-y-1.5 border-t border-gray-50 pt-3">
                {[
                  { label: t("payment.bank"), value: defaultBank.bankName },
                  { label: t("payment.account"), value: `**** ${defaultBank.accountNumber?.slice(-4)}` },
                  { label: t("payment.ifsc"), value: defaultBank.ifscCode },
                  { label: t("payment.name"), value: defaultBank.nickname },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between">
                    <p className="text-[12px] text-gray-400">{label}</p>
                    <p className="text-[12px] font-semibold text-[#1a2e1a]">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleFinalSubmit}
            disabled={loadings || available === 0}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ width: "calc(100% - 8px)", marginLeft: "4px", backgroundColor: "rgb(26,31,54)" }}
          >
            {available === 0 ? t("payment.noBalanceBtn") : t("payment.confirmTransfer")}
          </button>
        </div>
      </div>

      {/* ✅ Min Balance Popup */}
      {popup?.type === "minBalance" && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 text-center relative">
            <button
              onClick={() => setPopup(null)}
              className="absolute top-3 right-3 text-gray-400 text-xl"
            >
              ✖
            </button>
            <div className="w-16 h-16 mx-auto rounded-full bg-yellow-100 flex items-center justify-center mb-4 text-2xl">
              ⚠️
            </div>
            <h2 className="text-lg font-bold text-[#1A1F36] mb-2">
              {t("redeem.minBalanceTitle")}
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              {t("payment.noBalance")}
            </p>
            <button
              onClick={() => setPopup(null)}
              className="w-full py-3 rounded-xl text-white font-semibold"
              style={{ backgroundColor: "#1A1F36" }}
            >
              {t("common.done")}
            </button>
          </div>
        </div>
      )}

      {/* Success / Error Popup */}
      {popup && popup.type !== "minBalance" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center px-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-7 text-center">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                popup.type === "success" ? "bg-green-100" : "bg-red-100"
              }`}
            >
              {popup.type === "success" ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-500" />
              )}
            </div>

            <h2 className="text-[18px] font-bold text-[#1A1F36] mb-2">
              {popup.type === "success" ? t("common.success") : t("common.failed")}
            </h2>

            {popup.type === "success" && popup.amount > 0 && (
              <p className="text-[36px] font-bold text-green-600 leading-tight mb-2">
                ₹{popup.amount}
              </p>
            )}

            {popup.type === "success" && (
              <p className="text-[13px] text-gray-500 mb-4">
                {t("voucher.confirm_popupTitle")}
              </p>
            )}

            {popup.type === "error" && popup.message && (
              <p className="text-[13px] text-gray-500 mb-4">{popup.message}</p>
            )}

            <button
              onClick={handlePopupClose}
              className="w-full py-3.5 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform"
              style={{ backgroundColor: "rgb(26, 31, 54)" }}
            >
              {t("common.done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpiConfirmPage;